import React from "react";
import PropTypes from "prop-types";
import { companyUrl } from "views_shared/app/javascript/utils/urls";
import SavedGroupsBookmark from "../../source_directory/SavedGroupsBookmark";
import HashtagPills from "../../source_directory/HashtagPills";
import LogoTile from "../../source_directory/LogoTile";
import HighlightAttribute, { highlightValue } from "../../source_directory/HighlightAttribute";
import CardEmptyBody from "../../source_directory/CardEmptyBody";
import BodyTextWithExpand from "../../feeds/BodyTextWithExpand";

function CompanyCardGrid({ hit }) {
  const companyPath = companyUrl({ slug: hit.slug, migrate_id: hit.migrate_id });
  const description = hit.description || hit.headline || hit.experts_summary;
  // Only description and headline are searchable, so only those can carry a highlight; the
  // experts_summary fallback is shown plain.
  const descriptionAttribute = (hit.description && "description") || (hit.headline && "headline") || null;
  const hashtags = hit.hashtags || [];

  return (
    <div
      className="animated faster fadeIn"
      style={{
        gridRow: "span 4",
        display: "grid",
        gridTemplateRows: "subgrid",
        borderRadius: 10,
        border: "1px solid #e5e7eb",
        overflow: "hidden",
        backgroundColor: "#fff",
      }}
    >
      {/* Row 1: Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "16px 16px 0" }}>
        <LogoTile
          imageUrl={hit.profile_image_url}
          name={hit.name}
          size={56}
          borderRadius={10}
          fallbackBackground="#4f46e5"
          fallbackColor="#fff"
          fontSize={18}
          fontWeight={700}
        />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>
            <a href={companyPath} style={{ color: "#111827", textDecoration: "none" }}>
              <HighlightAttribute hit={hit} attribute="name" fallback={hit.name} />
            </a>
          </div>
        </div>
        <SavedGroupsBookmark
          sourceableType="Id::PromoEntity::Company"
          sourceableId={hit.id}
          sourceMeta={{ name: hit.name, imageUrl: hit.profile_image_url, slug: hit.slug }}
        />
      </div>

      {/* Row 2: Hashtags — its own subgrid track so the About row lines up across cards
          (an empty tags cell still occupies the track when a company has no hashtags). */}
      <div style={{ padding: "12px 16px 0" }}>
        {hashtags.length > 0 && <HashtagPills hashtags={hashtags} max={4} />}
      </div>

      {/* Row 3: About */}
      <div style={{ padding: "8px 16px 0" }}>
        {description ? (
          <>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>About:</div>
            <BodyTextWithExpand
              body={description}
              maxLength={130}
              highlightValue={descriptionAttribute ? highlightValue(hit, descriptionAttribute) : undefined}
              textClassName=""
              textStyle={{ fontSize: 13, color: "#374151", marginTop: 4, marginBottom: 0 }}
              toggleClassName="paragraph-xs"
            />
          </>
        ) : (
          <CardEmptyBody message="No company overview, yet." />
        )}
      </div>

      {/* Row 4: CTA */}
      <div style={{ padding: "12px 16px 16px" }}>
        <a
          href={companyPath}
          className="btn btn-primary w-100"
          style={{ background: "#4f46e5", border: "none", borderRadius: 8 }}
        >
          Learn More
        </a>
      </div>
    </div>
  );
}

CompanyCardGrid.propTypes = {
  hit: PropTypes.shape({
    slug: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    profile_image_url: PropTypes.string,
    description: PropTypes.string,
    headline: PropTypes.string,
    experts_summary: PropTypes.string,
    hashtags: PropTypes.arrayOf(
      PropTypes.shape({
        hashtag_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        hashtag_name: PropTypes.string,
        hashtag_slug: PropTypes.string,
      }),
    ),
  }).isRequired,
};

export default CompanyCardGrid;
